import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AsyncBoundary, ConfirmDialog, Drawer, Icon, useToast } from '@kollez/design-system';
import {
  useGroupPartnerById,
  useGroupPartnerMous,
  useGroupPartnerOpportunities,
  useUpdateGroupPartner,
  useDeactivateGroupPartner,
  useReactivateGroupPartner,
  useAddPartnerContact,
  useRemovePartnerContact,
  useRenewMoU,
  useGroupPartnerUsers,
  useInvitePartnerUser,
  useDeactivatePartnerUser,
  type CreatePartnerBody,
  type PartnerDetail,
  type PartnerContact,
} from '@/lib/queries';
import {
  PARTNER_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  PARTNER_INVITE_ROLE_OPTIONS,
  isHiringPartnerType,
} from '@/config/partnerOptions';

/**
 * /tpo/partners/:id — TPO-side partner detail with edit + deactivate + related content tabs.
 *
 * Tabs branch on partner.type:
 *   - hiring types (Product / Service / Consulting / Startup): Overview / Drives / MoUs / Contacts / Users
 *   - Training_Provider: Overview / Programs (coming-soon) / MoUs / Contacts / Users
 *   - Internship_Provider: Overview / Internships (coming-soon) / MoUs / Contacts / Users
 *   - other: Overview / MoUs / Contacts / Users
 */

type Tab = 'overview' | 'drives' | 'programs' | 'internships' | 'mous' | 'contacts' | 'users';

interface PartnerDetailExtended extends PartnerDetail {
  contacts?: PartnerContact[];
  createdAt?: string;
}

function statusBadgeClass(status?: string): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'active') return 'badge badge-success';
  if (s === 'inactive') return 'badge badge-warning';
  if (s === 'suspended') return 'badge badge-danger';
  return 'badge';
}

function mouStatusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s === 'active') return 'badge badge-success';
  if (s === 'signed') return 'badge badge-info';
  if (s === 'draft' || s === 'under_review') return 'badge badge-warning';
  if (s === 'expired' || s === 'terminated' || s === 'renewed') return 'badge';
  return 'badge';
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  return Math.round(ms / 86_400_000);
}

function initials(name?: string): string {
  return (name ?? '?').trim().split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase();
}

export function TpoPartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const toast = useToast();
  const detail = useGroupPartnerById(id, 'tpo');
  const mous = useGroupPartnerMous(id, 'tpo');
  const opps = useGroupPartnerOpportunities(id, 'tpo');
  const users = useGroupPartnerUsers(id, 'tpo');
  const update = useUpdateGroupPartner('tpo');
  const deactivate = useDeactivateGroupPartner('tpo');
  const reactivate = useReactivateGroupPartner('tpo');
  const addContact = useAddPartnerContact('tpo');
  const removeContact = useRemovePartnerContact('tpo');
  const renewMou = useRenewMoU();
  const inviteUser = useInvitePartnerUser('tpo');
  const deactivateUser = useDeactivatePartnerUser('tpo');

  const [tab, setTab] = useState<Tab>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', role: '', email: '', phone: '' });
  const [showRemoved, setShowRemoved] = useState(false);
  const [renewOpen, setRenewOpen] = useState<{ mouId: string; mouNumber?: string } | null>(null);
  const [renewForm, setRenewForm] = useState({ startsAt: '', endsAt: '' });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'recruiter', fullName: '' });
  const [inviteLinkModal, setInviteLinkModal] = useState<string | null>(null);
  const [confirmRemoveUser, setConfirmRemoveUser] = useState<{ userId: string; name: string } | null>(null);

  const partner: PartnerDetailExtended | undefined = detail.data?.partner as PartnerDetailExtended | undefined;
  const [editForm, setEditForm] = useState<CreatePartnerBody & { status?: string }>({
    name: '', type: 'Product_Company', industry: '', companySize: '', website: '', description: '',
    admin: { fullName: '', email: '' },
  });

  useEffect(() => {
    if (partner) {
      setEditForm({
        name: partner.name,
        type: partner.type,
        industry: partner.industry ?? '',
        companySize: partner.companySize ?? '',
        website: partner.website ?? '',
        description: partner.description ?? '',
        status: partner.status,
        admin: { fullName: '', email: '' },
      });
    }
  }, [partner]);

  const isHiring = isHiringPartnerType(partner?.type);
  const availableTabs = useMemo<Array<[Tab, string]>>(() => {
    if (!partner) return [['overview', 'Overview']];
    const base: Array<[Tab, string]> = [['overview', 'Overview']];
    if (isHiring) base.push(['drives', `Drives${opps.data ? ` · ${opps.data.opportunities.length}` : ''}`]);
    else if (partner.type === 'Training_Provider') base.push(['programs', 'Programs']);
    else if (partner.type === 'Internship_Provider') base.push(['internships', 'Internships']);
    base.push(['mous', `MoUs${mous.data ? ` · ${mous.data.mous.length}` : ''}`]);
    base.push(['contacts', `Contacts${partner.contacts ? ` · ${(partner.contacts ?? []).filter((c) => !c.removedAt).length}` : ''}`]);
    base.push(['users', `Users & Roles${users.data ? ` · ${users.data.members.length + users.data.pendingInvites.length}` : ''}`]);
    return base;
  }, [partner, isHiring, opps.data, mous.data, users.data]);

  useEffect(() => {
    if (partner && !availableTabs.find(([k]) => k === tab)) {
      setTab('overview');
    }
  }, [partner, availableTabs, tab]);

  const activeMouCount = (mous.data?.mous ?? []).filter((m) => m.status === 'Active').length;
  const drivesCount = opps.data?.opportunities.length ?? 0;
  const contactsCount = (partner?.contacts ?? []).filter((c) => !c.removedAt).length;
  const partnerSince = partner?.createdAt
    ? new Date(partner.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  function resetContact() { setContactForm({ name: '', role: '', email: '', phone: '' }); }
  function resetInvite() { setInviteForm({ email: '', role: 'recruiter', fullName: '' }); }

  return (
    <div className="stack">
      <AsyncBoundary
        kind="detail"
        loading={detail.isLoading}
        error={detail.error}
        empty={!detail.isLoading && !partner}
        emptyTitle="Partner not found"
        emptyHint="It may not be onboarded at your college, or it may have been removed."
      >
        {partner && (
          <>
            <div className="page-header">
              <div className="ph-text">
                <div className="flex items-center gap-2 mb-1">
                  <button className="btn btn-ghost btn-sm" onClick={() => nav('/tpo/partners')}>
                    <Icon name="chevron-left" size={14} /> All companies
                  </button>
                </div>
                <h1 className="h-page">{partner.name}</h1>
                <div className="flex flex-wrap gap-2 mt-1 items-center">
                  <span className="badge">{partner.type.replace(/_/g, ' ')}</span>
                  {partner.industry && <span className="badge">{partner.industry.replace(/_/g, ' ')}</span>}
                  {partner.companySize && <span className="badge">{partner.companySize}</span>}
                  <span className={statusBadgeClass(partner.status)}>{partner.status}</span>
                  {partner.isVerified && <span className="badge badge-info">Verified</span>}
                  {!isHiring && (
                    <span className="badge badge-warning" data-testid="limited-portal-chip">Limited portal</span>
                  )}
                </div>
              </div>
              <div className="ph-actions">
                <button className="btn btn-outline" onClick={() => setEditOpen(true)}>Edit</button>
                {partner.status === 'Active' ? (
                  <button className="btn btn-outline" onClick={() => setConfirmDeactivate(true)}>
                    <Icon name="x-circle" size={14} /> Deactivate
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => reactivate.mutate({ id: partner._id }, {
                      onSuccess: () => toast.show({ message: `${partner.name} reactivated.` }),
                    })}
                  >
                    <Icon name="circle-check" size={14} /> Reactivate
                  </button>
                )}
              </div>
            </div>

            {/* Overview stats — appears above tabs so it's always visible on hero */}
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              <div className="card kpi">
                <span className="kpi-label">{isHiring ? 'Drives' : 'Records'}</span>
                <span className="kpi-value">{drivesCount}</span>
                <span className="kpi-meta">this partner</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Active MoUs</span>
                <span className="kpi-value">{activeMouCount}</span>
                <span className="kpi-meta">{mous.data?.mous.length ?? 0} total</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Contacts</span>
                <span className="kpi-value">{contactsCount}</span>
                <span className="kpi-meta">POCs</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Partner since</span>
                <span className="kpi-value" style={{ fontSize: '1.125rem' }}>{partnerSince ?? '—'}</span>
                <span className="kpi-meta">at your college</span>
              </div>
            </div>

            <div className="segmented">
              {availableTabs.map(([k, label]) => (
                <button key={k} aria-pressed={tab === k} onClick={() => setTab(k)}>{label}</button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="card p-4">
                <dl className="dl">
                  <dt>Type</dt><dd>{partner.type.replace(/_/g, ' ')}</dd>
                  <dt>Industry</dt><dd>{partner.industry?.replace(/_/g, ' ') ?? '—'}</dd>
                  <dt>Company size</dt><dd>{partner.companySize ?? '—'}</dd>
                  <dt>Website</dt><dd>{partner.website ? <a className="link" href={partner.website} target="_blank" rel="noreferrer">{partner.website}</a> : '—'}</dd>
                  <dt>Status</dt><dd><span className={statusBadgeClass(partner.status)}>{partner.status}</span></dd>
                </dl>
                {partner.description && (
                  <>
                    <h3 className="text-sm font-semibold mt-4 mb-2">About</h3>
                    <p className="text-sm">{partner.description}</p>
                  </>
                )}
              </div>
            )}

            {tab === 'drives' && (
              <div className="card">
                <AsyncBoundary
                  kind="table"
                  inCard
                  loading={opps.isLoading}
                  error={opps.error}
                  empty={!opps.isLoading && (opps.data?.opportunities.length ?? 0) === 0}
                  emptyTitle="No drives from this partner yet"
                  emptyHint="Once this partner posts an opportunity for your college, it shows up here."
                >
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th className="hide-mobile">Openings</th>
                          <th className="hide-mobile">Posted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(opps.data?.opportunities ?? []).map((o) => (
                          <tr key={o._id}>
                            <td>{o.title}</td>
                            <td>{o.role ?? '—'}</td>
                            <td><span className={o.status === 'Active' ? 'badge badge-success' : 'badge'}>{o.status}</span></td>
                            <td className="hide-mobile">{o.openings ?? '—'}</td>
                            <td className="hide-mobile">{o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AsyncBoundary>
              </div>
            )}

            {tab === 'programs' && (
              <div className="card p-6" data-testid="programs-coming-soon">
                <h2 className="h-section m-0 mb-2">Training programs</h2>
                <p className="helper m-0">
                  Programs tracking (courses, enrollments, completions, certifications) is on our
                  roadmap. For now, work directly with your TPO to log these records manually.
                </p>
              </div>
            )}

            {tab === 'internships' && (
              <div className="card p-6" data-testid="internships-coming-soon">
                <h2 className="h-section m-0 mb-2">Internships</h2>
                <p className="helper m-0">
                  Internship tracking (start/end dates, monthly stipends, conversions to full-time)
                  is on our roadmap. For now, work directly with your TPO to log these records.
                </p>
              </div>
            )}

            {tab === 'mous' && (
              <div className="card">
                <AsyncBoundary
                  kind="table"
                  inCard
                  loading={mous.isLoading}
                  error={mous.error}
                  empty={!mous.isLoading && (mous.data?.mous.length ?? 0) === 0}
                  emptyTitle="No MoUs with this partner"
                  emptyHint="Sign an MoU so this partner's private drives are visible to your students."
                >
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>MoU number</th>
                          <th>Kind</th>
                          <th>Status</th>
                          <th>Term ends</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(mous.data?.mous ?? []).map((m) => {
                          const d = daysUntil(m.term.endsAt);
                          const canRenew = m.status === 'Active' && d !== null && d <= 60;
                          return (
                            <tr key={m._id}>
                              <td>
                                <Link className="link" to={`/group/mous/${m._id}`}>{m.mouNumber ?? m._id.slice(-6)}</Link>
                              </td>
                              <td>{m.kind ?? '—'}</td>
                              <td><span className={mouStatusBadgeClass(m.status)}>{m.status}</span></td>
                              <td>
                                {new Date(m.term.endsAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                {d !== null && d >= 0 && d <= 60 && (
                                  <span className="badge badge-warning ml-2">{d}d left</span>
                                )}
                                {d !== null && d < 0 && <span className="badge badge-danger ml-2">Expired</span>}
                              </td>
                              <td>
                                {canRenew ? (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    data-testid="mou-renew-btn"
                                    onClick={() => setRenewOpen({ mouId: m._id, mouNumber: m.mouNumber })}
                                  >Renew</button>
                                ) : m.status === 'Renewed' ? (
                                  <span className="helper">Renewed</span>
                                ) : (
                                  <span className="helper">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </AsyncBoundary>
              </div>
            )}

            {tab === 'contacts' && (
              <div className="card">
                <div className="p-3 flex items-center gap-2">
                  <h3 className="text-sm font-semibold m-0">Contacts</h3>
                  <button
                    className="btn btn-sm btn-outline ml-auto"
                    data-testid="add-contact-btn"
                    onClick={() => setAddContactOpen(true)}
                  >
                    <Icon name="plus" size={14} /> Add contact
                  </button>
                  {(partner.contacts ?? []).some((c) => c.removedAt) && (
                    <label className="flex items-center gap-1 text-sm">
                      <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
                      Show removed
                    </label>
                  )}
                </div>
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr><th>Name</th><th>Role</th><th>Email</th><th className="hide-mobile">Phone</th><th></th></tr>
                    </thead>
                    <tbody>
                      {(partner.contacts ?? [])
                        .filter((c) => showRemoved || !c.removedAt)
                        .map((c) => (
                          <tr key={c._id} style={c.removedAt ? { opacity: 0.55 } : undefined}>
                            <td>{c.name}</td>
                            <td>{c.role ? <span className="badge">{c.role}</span> : '—'}</td>
                            <td><a className="link" href={`mailto:${c.email}`}>{c.email}</a></td>
                            <td className="hide-mobile">{c.phone ? <a className="link" href={`tel:${c.phone}`}>{c.phone}</a> : '—'}</td>
                            <td>
                              {c.removedAt ? (
                                <span className="helper">Removed</span>
                              ) : (
                                <button
                                  className="btn btn-sm btn-ghost"
                                  onClick={() => {
                                    if (!id) return;
                                    removeContact.mutate({ partnerId: id, contactId: c._id }, {
                                      onSuccess: () => toast.show({ message: `Removed ${c.name}.` }),
                                      onError: (err) => toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't remove." }),
                                    });
                                  }}
                                >Remove</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      {(partner.contacts ?? []).filter((c) => showRemoved || !c.removedAt).length === 0 && (
                        <tr><td colSpan={5} className="text-center helper p-4">No contacts yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'users' && (
              <div className="card">
                <div className="p-3 flex items-center gap-2">
                  <h3 className="text-sm font-semibold m-0">Users & Roles</h3>
                  <button
                    className="btn btn-sm btn-primary ml-auto"
                    data-testid="invite-user-btn"
                    onClick={() => setInviteOpen(true)}
                  >
                    <Icon name="user-plus" size={14} /> Invite user
                  </button>
                </div>
                <AsyncBoundary
                  kind="table"
                  inCard
                  loading={users.isLoading}
                  error={users.error}
                  empty={!users.isLoading && (users.data?.members.length ?? 0) === 0 && (users.data?.pendingInvites.length ?? 0) === 0}
                  emptyTitle="No users yet"
                  emptyHint="Invite the partner's admin and their team so they can post drives and review candidates on Kollez."
                >
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr><th>Person</th><th>Role</th><th>Status</th><th></th></tr>
                      </thead>
                      <tbody>
                        {(users.data?.members ?? []).map((u) => (
                          <tr key={u._id}>
                            <td>
                              <div className="flex items-center gap-3">
                                <span className="avatar">{initials(u.fullName)}</span>
                                <div>
                                  <div className="cell-title">{u.fullName}</div>
                                  <div className="cell-sub">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className="badge">{u.role.replace(/_/g, ' ')}</span></td>
                            <td>
                              <span className="badge badge-success">Active</span>
                              {u.lastLoginAt && <span className="helper ml-2">last {new Date(u.lastLoginAt).toLocaleDateString('en-IN')}</span>}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => setConfirmRemoveUser({ userId: u._id, name: u.fullName })}
                              >Remove</button>
                            </td>
                          </tr>
                        ))}
                        {(users.data?.pendingInvites ?? []).map((inv) => (
                          <tr key={inv._id} style={{ opacity: 0.8 }}>
                            <td>
                              <div className="flex items-center gap-3">
                                <span className="avatar">{initials(inv.fullName ?? inv.email)}</span>
                                <div>
                                  <div className="cell-title">{inv.fullName ?? inv.email}</div>
                                  <div className="cell-sub">{inv.email}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className="badge">{inv.role.replace(/_/g, ' ')}</span></td>
                            <td><span className="badge badge-warning">Invite sent</span></td>
                            <td><span className="helper">expires {new Date(inv.expiresAt).toLocaleDateString('en-IN')}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AsyncBoundary>
              </div>
            )}
          </>
        )}
      </AsyncBoundary>

      {partner && (
        <Drawer open={editOpen} onClose={() => setEditOpen(false)} title="Edit partner" size="md">
          <div className="grid gap-4">
            <label className="grid gap-1.5">
              <span className="label">Name</span>
              <input className="input" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Type</span>
              <select className="select" value={editForm.type} onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}>
                {PARTNER_TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-1.5">
                <span className="label">Industry</span>
                <select className="select" value={editForm.industry ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, industry: e.target.value }))}>
                  {INDUSTRY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="grid gap-1.5">
                <span className="label">Company size</span>
                <select className="select" value={editForm.companySize ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, companySize: e.target.value }))}>
                  {COMPANY_SIZE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
            <label className="grid gap-1.5">
              <span className="label">Website</span>
              <input className="input" type="url" value={editForm.website ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, website: e.target.value }))} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Description</span>
              <textarea className="input" rows={3} value={editForm.description ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} />
            </label>
          </div>
          <div className="drawer-foot">
            <button className="btn btn-outline" onClick={() => setEditOpen(false)} disabled={update.isPending}>Cancel</button>
            <button
              className="btn btn-primary ml-auto"
              disabled={update.isPending}
              onClick={() => {
                update.mutate({
                  id: partner._id,
                  body: {
                    name: editForm.name,
                    type: editForm.type,
                    industry: editForm.industry || undefined,
                    companySize: editForm.companySize || undefined,
                    website: editForm.website || undefined,
                    description: editForm.description || undefined,
                  },
                }, {
                  onSuccess: () => { toast.show({ message: 'Partner updated.' }); setEditOpen(false); },
                  onError: (err) => { toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't update." }); },
                });
              }}
            >{update.isPending ? 'Saving…' : 'Save changes'}</button>
          </div>
        </Drawer>
      )}

      <Drawer open={addContactOpen} onClose={() => { setAddContactOpen(false); resetContact(); }} title="Add contact" size="md">
        <div className="grid gap-4">
          <label className="grid gap-1.5"><span className="label">Name *</span>
            <input className="input" value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} />
          </label>
          <label className="grid gap-1.5"><span className="label">Role</span>
            <input className="input" value={contactForm.role} onChange={(e) => setContactForm((f) => ({ ...f, role: e.target.value }))} placeholder="e.g. Campus recruiter" />
          </label>
          <label className="grid gap-1.5"><span className="label">Email *</span>
            <input className="input" type="email" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
          </label>
          <label className="grid gap-1.5"><span className="label">Phone</span>
            <input className="input" value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} />
          </label>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => { setAddContactOpen(false); resetContact(); }} disabled={addContact.isPending}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled={addContact.isPending || contactForm.name.trim().length < 2 || !contactForm.email.trim()}
            onClick={() => {
              if (!id) return;
              addContact.mutate({
                partnerId: id,
                body: {
                  name: contactForm.name.trim(),
                  role: contactForm.role.trim() || undefined,
                  email: contactForm.email.trim().toLowerCase(),
                  phone: contactForm.phone.trim() || undefined,
                },
              }, {
                onSuccess: () => { toast.show({ message: 'Contact added.' }); setAddContactOpen(false); resetContact(); },
                onError: (err) => toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't add." }),
              });
            }}
          >{addContact.isPending ? 'Adding…' : 'Add contact'}</button>
        </div>
      </Drawer>

      <Drawer open={!!renewOpen} onClose={() => { setRenewOpen(null); setRenewForm({ startsAt: '', endsAt: '' }); }} title={renewOpen ? `Renew ${renewOpen.mouNumber ?? 'MoU'}` : 'Renew MoU'} size="md">
        <div className="grid gap-4">
          <p className="helper m-0">This creates a fresh MoU (Draft) referencing the old as its predecessor. The old MoU transitions to `Renewed` automatically.</p>
          <label className="grid gap-1.5"><span className="label">New term starts</span>
            <input className="input" type="date" value={renewForm.startsAt} onChange={(e) => setRenewForm((f) => ({ ...f, startsAt: e.target.value }))} />
          </label>
          <label className="grid gap-1.5"><span className="label">New term ends</span>
            <input className="input" type="date" value={renewForm.endsAt} onChange={(e) => setRenewForm((f) => ({ ...f, endsAt: e.target.value }))} />
          </label>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => { setRenewOpen(null); setRenewForm({ startsAt: '', endsAt: '' }); }} disabled={renewMou.isPending}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled={renewMou.isPending || !renewForm.startsAt || !renewForm.endsAt || !renewOpen}
            onClick={() => {
              if (!renewOpen) return;
              renewMou.mutate({
                mouId: renewOpen.mouId,
                body: { term: { startsAt: renewForm.startsAt, endsAt: renewForm.endsAt } },
              }, {
                onSuccess: (r) => {
                  toast.show({ message: `Renewed. New MoU ${r.mou.mouNumber ?? ''} created.` });
                  setRenewOpen(null); setRenewForm({ startsAt: '', endsAt: '' });
                },
                onError: (err) => toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't renew." }),
              });
            }}
          >{renewMou.isPending ? 'Renewing…' : 'Create renewal MoU'}</button>
        </div>
      </Drawer>

      <Drawer open={inviteOpen} onClose={() => { setInviteOpen(false); resetInvite(); }} title="Invite user" size="md">
        <div className="grid gap-4">
          <p className="helper m-0">They'll get an invite link to activate their account and join this partner's Kollez workspace.</p>
          <label className="grid gap-1.5"><span className="label">Full name</span>
            <input className="input" value={inviteForm.fullName} onChange={(e) => setInviteForm((f) => ({ ...f, fullName: e.target.value }))} />
          </label>
          <label className="grid gap-1.5"><span className="label">Email *</span>
            <input className="input" type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
          </label>
          <label className="grid gap-1.5"><span className="label">Role *</span>
            <select className="select" value={inviteForm.role} onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}>
              {PARTNER_INVITE_ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => { setInviteOpen(false); resetInvite(); }} disabled={inviteUser.isPending}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled={inviteUser.isPending || !inviteForm.email.trim()}
            onClick={() => {
              if (!id) return;
              inviteUser.mutate({
                partnerId: id,
                body: {
                  email: inviteForm.email.trim().toLowerCase(),
                  role: inviteForm.role,
                  fullName: inviteForm.fullName.trim() || undefined,
                },
              }, {
                onSuccess: (res) => {
                  toast.show({ message: 'Invite generated.' });
                  setInviteLinkModal(res.inviteLink);
                  setInviteOpen(false); resetInvite();
                },
                onError: (err) => toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't invite." }),
              });
            }}
          >{inviteUser.isPending ? 'Sending…' : 'Send invite'}</button>
        </div>
      </Drawer>

      {inviteLinkModal && (
        <div className="card p-4" data-testid="user-invite-link-block">
          <h3 className="text-sm font-semibold m-0 mb-1">Invite link (test only)</h3>
          <code style={{ display: 'block', wordBreak: 'break-all', padding: 6, background: 'rgb(var(--bg-tertiary))', borderRadius: 6 }}>
            {inviteLinkModal}
          </code>
          <button className="btn btn-outline mt-2" onClick={() => setInviteLinkModal(null)}>Dismiss</button>
        </div>
      )}

      {confirmRemoveUser && (
        <ConfirmDialog
          open={!!confirmRemoveUser}
          onClose={() => setConfirmRemoveUser(null)}
          title={`Remove ${confirmRemoveUser.name}?`}
          description="They'll be signed out and won't be able to sign in until you invite them again."
          typeToConfirm="remove"
          confirmLabel="Remove"
          danger
          onConfirm={async () => {
            if (!id || !confirmRemoveUser) return;
            await deactivateUser.mutateAsync({ partnerId: id, userId: confirmRemoveUser.userId })
              .then(() => toast.show({ message: `${confirmRemoveUser.name} removed.` }))
              .catch((err) => toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't remove." }))
              .finally(() => setConfirmRemoveUser(null));
          }}
        />
      )}

      {partner && (
        <ConfirmDialog
          open={confirmDeactivate}
          onClose={() => setConfirmDeactivate(false)}
          title="Deactivate this partner?"
          description="They won't appear in the active list anymore. Existing MoUs and drives stay for the audit trail. Reactivate anytime."
          typeToConfirm="deactivate"
          confirmLabel="Deactivate"
          danger
          onConfirm={async () => {
            await deactivate.mutateAsync({ id: partner._id })
              .then(() => toast.show({ message: `${partner.name} deactivated.` }))
              .catch((err) => toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't deactivate." }));
          }}
        />
      )}
    </div>
  );
}

export default TpoPartnerDetailPage;
